import { useState } from 'react';

import {
  ArrowRight,
  Building2,
  CheckCircle,
  Factory,
  GraduationCap,
  Heart,
  Landmark,
  ShoppingCart,
  Target,
} from 'lucide-react';
import { motion } from 'motion/react';

import { Button } from '@src/components/ui/button';
import { Card, CardContent } from '@src/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@src/components/ui/tabs';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const industries = [
  {
    id: 'financial',
    name: 'Financial Services',
    icon: Landmark,
    color: 'from-blue-500 to-blue-600',
    challenges: [
      'Complex regulatory requirements (SOX, PCI DSS)',
      'Sensitive financial data protection',
      'Real-time fraud detection needs',
      'Audit trail requirements',
    ],
    solutions: [
      'Automated SOX compliance workflows',
      'PCI DSS evidence collection',
      'Real-time risk monitoring',
      'Comprehensive audit trails',
      'Identity access governance',
    ],
    capabilities: ['SOX Ready', 'PCI DSS Automation', 'Real-time Monitoring'],
    whyBOSSS: 'Purpose-built for financial services compliance and security requirements',
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    icon: Heart,
    color: 'from-red-500 to-red-600',
    challenges: [
      'HIPAA compliance complexity',
      'Patient data security',
      'Medical device management',
      'Third-party risk assessment',
    ],
    solutions: [
      'HIPAA-ready compliance framework',
      'PHI access controls and monitoring',
      'Medical device inventory tracking',
      'Vendor risk assessments',
      'Breach notification automation',
    ],
    capabilities: ['HIPAA Ready', 'PHI Protection', 'Device Tracking'],
    whyBOSSS: 'Comprehensive healthcare security and compliance platform',
  },
  {
    id: 'education',
    name: 'Education',
    icon: GraduationCap,
    color: 'from-green-500 to-green-600',
    challenges: [
      'FERPA student record protection',
      'Limited cybersecurity budgets',
      'Diverse user populations',
      'Legacy system integration',
    ],
    solutions: [
      'FERPA compliance automation',
      'Cost-effective security controls',
      'Student/faculty identity management',
      'Legacy system risk assessment',
      'Incident response planning',
    ],
    capabilities: ['FERPA Ready', 'Budget-Friendly', 'Identity Management'],
    whyBOSSS: 'Affordable security and compliance for educational institutions',
  },
  {
    id: 'government',
    name: 'Government',
    icon: Building2,
    color: 'from-purple-500 to-purple-600',
    challenges: [
      'FedRAMP and FISMA requirements',
      'Citizen data protection',
      'Inter-agency coordination',
      'Budget constraints',
    ],
    solutions: [
      'FedRAMP-ready security controls',
      'Citizen privacy protection',
      'Multi-agency governance',
      'Cost-optimized deployments',
      'Continuous monitoring',
    ],
    capabilities: ['FedRAMP Ready', 'FISMA Compliant', 'Continuous Monitoring'],
    whyBOSSS: 'Government-grade security and compliance automation',
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: Factory,
    color: 'from-orange-500 to-orange-600',
    challenges: [
      'OT/IT security convergence',
      'Supply chain risk',
      'Industrial control systems',
      'Regulatory compliance',
    ],
    solutions: [
      'OT/IT unified monitoring',
      'Supply chain risk assessment',
      'ICS security controls',
      'Industry-specific compliance',
      'Asset discovery and tracking',
    ],
    capabilities: ['OT/IT Unified', 'ICS Security', 'Supply Chain Risk'],
    whyBOSSS: 'Unified security for modern manufacturing environments',
  },
  {
    id: 'retail',
    name: 'Retail',
    icon: ShoppingCart,
    color: 'from-pink-500 to-pink-600',
    challenges: [
      'PCI DSS compliance',
      'Customer data protection',
      'Multi-location security',
      'Seasonal workforce management',
    ],
    solutions: [
      'PCI DSS automation',
      'Customer privacy controls',
      'Multi-site governance',
      'Temporary access management',
      'Point-of-sale security',
    ],
    capabilities: ['PCI DSS Ready', 'Multi-Location', 'Privacy Controls'],
    whyBOSSS: 'Scalable security for retail operations of any size',
  },
  {
    id: 'mssp',
    name: 'MSSPs & MSPs',
    icon: Building2,
    color: 'from-teal-500 to-teal-600',
    challenges: [
      'Multi-tenant client management',
      'Scalable security operations',
      'Diverse compliance requirements',
      'White-label reporting needs',
    ],
    solutions: [
      'Multi-tenant architecture by design',
      'Entity switching for seamless client management',
      'Consolidated cross-client reporting',
      'White-label branding options',
      '10 AI voice agents to scale expertise',
    ],
    capabilities: ['Multi-Tenant', 'White-Label', 'Scalable SOC'],
    whyBOSSS: 'One platform to manage security for all your clients',
  },
];

export function IndustriesSection() {
  const [activeIndustry, setActiveIndustry] = useState('financial');
  const currentIndustry = industries.find((ind) => ind.id === activeIndustry) || industries[0];
  const IconComponent = currentIndustry.icon;
  const onNavigateToTop = useNavigateToTop();
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-16"
          transition={{ duration: 0.8 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Target className="w-4 h-4 mr-2" />
            Industry-Specific Solutions
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Tailored for Your Industry
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            BOSSS understands that every industry has unique compliance requirements and security challenges.
            Our platform adapts to your specific needs with pre-configured frameworks and industry expertise.
          </p>
        </motion.div>

        <Tabs className="w-full" value={activeIndustry} onValueChange={setActiveIndustry}>
          {/* Industry Tabs */}
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 mb-12 bg-gray-100 p-1 rounded-xl gap-1">
            {industries.map((industry) => {
              const TabIcon = industry.icon;
              return (
                <TabsTrigger
                  key={industry.id}
                  value={industry.id}
                  className="flex flex-col items-center p-2 sm:p-4 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
                >
                  <TabIcon className="w-5 h-5 sm:w-6 sm:h-6 mb-1 sm:mb-2" />
                  <span className="text-xs sm:text-sm font-medium text-center">{industry.name}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Industry Content */}
          {industries.map((industry) => (
            <TabsContent key={industry.id} value={industry.id}>
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                initial={{ opacity: 0, y: 20 }}
                className="grid grid-cols-1 lg:grid-cols-3 gap-8"
              >
                {/* Industry Overview */}
                <Card className="lg:col-span-1">
                  <CardContent className="p-8">
                    <div className={`w-16 h-16 bg-gradient-to-br ${industry.color} rounded-xl flex items-center justify-center mb-6`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>

                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{industry.name}</h3>

                    {/* Key Capabilities */}
                    <div className="space-y-3 mb-6">
                      {industry.capabilities.map((capability, idx) => (
                        <div key={idx} className="flex items-center">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                          <span className="text-gray-700 text-sm font-medium">{capability}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600">{industry.whyBOSSS}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Challenges & Solutions */}
                <div className="lg:col-span-2 space-y-8">
                  <Card>
                    <CardContent className="p-8">
                      <h4 className="text-xl font-bold text-gray-900 mb-6">Common Challenges</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {industry.challenges.map((challenge, idx) => (
                          <motion.div
                            key={idx}
                            animate={{ opacity: 1, x: 0 }}
                            initial={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3, delay: idx * 0.1 }}
                            className="flex items-start bg-red-50 rounded-lg p-4"
                          >
                            <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                            <span className="text-gray-700">{challenge}</span>
                          </motion.div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-8">
                      <h4 className="text-xl font-bold text-gray-900 mb-6">BOSSS Solutions</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {industry.solutions.map((solution, idx) => (
                          <motion.div
                            key={idx}
                            animate={{ opacity: 1, x: 0 }}
                            initial={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3, delay: idx * 0.1 }}
                            className="flex items-start bg-green-50 rounded-lg p-4"
                          >
                            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                            <span className="text-gray-700">{solution}</span>
                          </motion.div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Bottom CTA */}
        <motion.div
          viewport={{ once: true }}
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              See How BOSSS Works for Your Industry
            </h3>

            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Schedule a personalized demo to see how BOSSS can address your industry-specific
              compliance and security challenges with tailored solutions.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/demo')}
              >
                Request Industry Demo
              </Button>

              <Button
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/contact')}
              >
                Talk to an Expert
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
